import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MarkdownContent from './MarkdownContent';
import { useReport } from '../contexts/ReportContext';
import { formatBackendDateTimeLocal } from '../utils/formatBackendDateTimeLocal';

function extractTimeLabel(formattedTimestamp: string, rawTimestamp: string): string {
  const formattedMatch = String(formattedTimestamp).match(/\b\d{2}:\d{2}:\d{2}\b/);
  if (formattedMatch) return formattedMatch[0];

  const rawMatch = String(rawTimestamp).match(/\b\d{2}:\d{2}:\d{2}\b/);
  if (rawMatch) return rawMatch[0];

  return rawTimestamp;
}

const SummaryContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
`;

const SubTabNav = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    transition: background ${({ theme }) => theme.transitions.base};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SubTabButton = styled.button<{ active: boolean }>`
  flex: 0 0 auto;
  min-width: fit-content;
  padding: 0.75rem 1rem;
  border: none;
  background: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active, theme }) => active ? 'white' : theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all ${({ theme }) => theme.transitions.base};
  margin-right: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primaryHover : theme.colors.surfaceSecondary};
  }
`;

const SubTabContent = styled.div`
  min-height: 300px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 300px;
  margin: 0;
`;

const SummaryContent = styled.div`
  font-family: ${({ theme }) => theme.fonts.base};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  overflow-x: auto;

  h1 {
    font-size: 2rem;
    font-weight: 800;
    margin: 2rem 0 1.5rem 0;
    padding-bottom: 1rem;
    border-bottom: 3px solid ${({ theme }) => theme.colors.primaryLight};
    color: ${({ theme }) => theme.colors.textPrimary};
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryHover});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 1.5rem 0 1rem 0;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 1.25rem 0 0.75rem 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin-bottom: 1.25rem;
    font-size: 1rem;
    line-height: 1.7;
  }

  ul, ol {
    padding-left: 1.5rem;
    margin-bottom: 1.25rem;
  }

  li {
    margin-bottom: 0.5rem;
    font-size: 1rem;
    line-height: 1.6;
  }

  code {
    background: ${({ theme }) => theme.colors.primaryLight};
    padding: 0.25rem 0.5rem;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }

  pre {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.surfaceSecondary}, ${({ theme }) => theme.colors.borderLight});
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: 1.5rem;
    margin: 1.5rem 0;
    overflow-x: auto;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 0.875rem;
    line-height: 1.5;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  th, td {
    padding: 1rem;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    font-size: 0.9rem;
    text-align: left;
  }

  th {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.surfaceSecondary}, ${({ theme }) => theme.colors.borderLight});
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const SummaryTab: React.FC = () => {
  const { reportState, isAutoTriggeringFromUrl, hasChatStarted } = useReport();
  const [activeSubTab, setActiveSubTab] = useState('latest');
  
  const analysisHistory = reportState.analysisHistory;

  // 仕切り直し・中止で内容が空に戻ったら、選んでいた「履歴 N」も「最新の概要」に戻す
  useEffect(() => {
    if (!reportState.overview) setActiveSubTab('latest');
  }, [reportState.overview]);

  // 本文つきの「新しいインシデント」では、概要は空を経ずに次のインシデントの概要へ変わり、分析履歴は
  // 前より少ない件数で置き換わる。件数が減ったら、選んでいた「履歴 N」も「最新の概要」に戻す
  const historyCount = analysisHistory.length;
  const prevHistoryCountRef = useRef(historyCount);
  useEffect(() => {
    if (historyCount < prevHistoryCountRef.current) setActiveSubTab('latest');
    prevHistoryCountRef.current = historyCount;
  }, [historyCount]);

  const hasSummaryData = !!reportState.overview;
  const shouldShowSummary = hasSummaryData && (hasChatStarted || isAutoTriggeringFromUrl);

  if (!shouldShowSummary) {
    return (
      <SummaryContainer>
        <EmptyState>
          <EmptyStateIcon>
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 2v10h8V6H6z" />
              <path d="M8 8h4v2H8V8zm0 4h4v2H8v-2z" />
            </svg>
          </EmptyStateIcon>
          <EmptyStateTitle>インシデント概要</EmptyStateTitle>
          <EmptyStateDescription>
            チャットでインシデントについて質問すると、ここに概要が表示されます。
          </EmptyStateDescription>
        </EmptyState>
      </SummaryContainer>
    );
  }

  const renderSubTabContent = () => {
    if (activeSubTab === 'latest') {
      return (
        <SummaryContent>
          <MarkdownContent>{reportState.overview || ''}</MarkdownContent>
        </SummaryContent>
      );
    }
    
    const historyIndex = parseInt(activeSubTab.replace('history-', ''));
    const historyItem = analysisHistory[historyIndex];
    if (historyItem) {
      const displayTimestamp = formatBackendDateTimeLocal(historyItem.timestamp);
      return (
        <SummaryContent>
          <div style={{ marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: `1px solid #e2e8f0` }}>
            <h6 style={{ color: '#3b82f6', margin: '0 0 0.5rem 0' }}>{historyItem.stage}</h6>
            <small style={{ color: '#64748b' }}>{displayTimestamp}</small>
          </div>
          <MarkdownContent>{historyItem.content}</MarkdownContent>
        </SummaryContent>
      );
    }
    
    return null;
  };

  return (
    <SummaryContainer>
      <SubTabNav>
        <SubTabButton
          active={activeSubTab === 'latest'}
          onClick={() => setActiveSubTab('latest')}
        >
          最新の概要
        </SubTabButton>
        {analysisHistory.map((item, index) => {
          const displayTimestamp = formatBackendDateTimeLocal(item.timestamp);
          const timePart = extractTimeLabel(displayTimestamp, item.timestamp);
          return (
          <SubTabButton
            key={index}
            active={activeSubTab === `history-${index}`}
            onClick={() => setActiveSubTab(`history-${index}`)}
            title={displayTimestamp}
          >
            履歴{index + 1}: {timePart}
          </SubTabButton>
          );
        })}
      </SubTabNav>
      
      <SubTabContent>
        {renderSubTabContent()}
      </SubTabContent>
    </SummaryContainer>
  );
};

export default SummaryTab;